import {Button} from '@wandb/weave/components';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {type FC, useContext} from 'react';

import {formatAsDollar} from '../../../pages/Billing/AccountSettings/BillingTab/PlanCard/PlanCardComponents';
import {
  createDismissibleBanner,
  DEFAULT_BANNER_SHOW_AFTER,
} from '../../../pages/Billing/AccountSettings/WarningBanner/DismissibleBanner';
import {isAfter} from '../../../util/date';
import {useGetSandboxesUsageDetails} from '../../../util/usage/sandboxes';
import {
  AccountStatusBanner,
  AccountStatusBannerBoldText,
  AccountStatusBannerFlexWrapper,
  AccountStatusBannerTextWrapper,
} from '../../AccountStatusBanner';
import {AccountSelectorContext} from '../../Search/SearchNav/AccountSelectorContextProvider';
import {AccountPlanType} from '../../Search/SearchNav/types';
import {
  ActivateCTA,
  ContactSalesCTA,
  ContactSupportCTA,
  UpgradePlanCTA,
} from '../InferenceEnforcementBanners/utils';
import type {
  SandboxesBannerState,
  SandboxesEnforcementBannerProps,
} from './types';

const TODAY = new Date();

export const SandboxesEnforcementBannerGlobalList: FC = () => {
  const {selectedAccount} = useContext(AccountSelectorContext);

  const {
    isUsagePaid,
    isBillingStatusLoading,
    sandboxesLimits,
    billingPeriodEnd,
    usage,
    isVisible,
    loading,
    error,
  } = useGetSandboxesUsageDetails(selectedAccount);

  if (loading || isBillingStatusLoading || error || !isVisible) {
    return null;
  }
  if (selectedAccount == null) {
    return null;
  }

  const planType = selectedAccount.accountPlanType;
  const isTrial = planType === AccountPlanType.ThirtyDayTrial;
  const isAcademic = planType === AccountPlanType.Academic;
  const isFree = planType === AccountPlanType.Free;
  const isPro = planType === AccountPlanType.Pro;
  const isEnterprise = planType === AccountPlanType.Enterprise;

  const {sandboxesFreeLimit, sandboxesSafetyLimit} = sandboxesLimits;

  const states = getSandboxesBannerStates({
    usage,
    freeLimit: sandboxesFreeLimit,
    safetyLimit: sandboxesSafetyLimit,
    isUsagePaid,
    hasSpendCap: isAcademic || isPro || isEnterprise || (isFree && isUsagePaid),
    hasCreditStates: isTrial || isAcademic || isEnterprise,
    hasSelfServePaygo: !isEnterprise,
    isCreditPeriodExpired: isAfter(TODAY, billingPeriodEnd),
  });

  if (states.length === 0) {
    return null;
  }

  const organizationId = selectedAccount.id ?? '';

  const renderBanner = (state: SandboxesBannerState) => {
    const shared = {organizationId, bannerState: state, planName: planType};
    switch (state) {
      case 'spendCapReached':
        return (
          <SandboxesEnforcementBanner
            key={state}
            {...shared}
            boldStatusMsg={`Your monthly Sandbox spend limit of ${formatAsDollar(
              sandboxesSafetyLimit / 100
            )} has been reached.`}
            statusMsg={
              isFree
                ? 'New sandboxes cannot start, and running sandboxes have been stopped. Upgrade to Pro for a higher monthly limit.'
                : 'New sandboxes cannot start, and running sandboxes have been stopped. To request a limit increase, contact support.'
            }
            cta={
              isFree ? (
                <UpgradePlanCTA label="Upgrade to Pro" />
              ) : (
                <ContactSupportCTA label="Contact support" />
              )
            }
          />
        );
      case 'paused':
        return (
          <SandboxesEnforcementBanner
            key={state}
            {...shared}
            boldStatusMsg="Your Sandbox credits have been fully used and sandboxes are paused. Activate pay-as-you-go to resume."
            cta={<ActivateCTA product="sandboxes" />}
          />
        );
      case 'creditsExhaustedContactSales':
        return (
          <SandboxesEnforcementBanner
            key={state}
            {...shared}
            boldStatusMsg="Your Sandbox credits have been fully used and sandboxes are paused."
            statusMsg="Contact your account team to continue."
            cta={<ContactSalesCTA />}
          />
        );
    }
  };

  return (
    <Tailwind>
      <div>{states.map(renderBanner)}</div>
    </Tailwind>
  );
};

export function getSandboxesBannerStates({
  usage,
  freeLimit,
  safetyLimit,
  isUsagePaid,
  hasSpendCap,
  hasCreditStates,
  hasSelfServePaygo,
  isCreditPeriodExpired,
}: {
  usage: number;
  freeLimit: number;
  safetyLimit: number;
  isUsagePaid: boolean;
  hasSpendCap: boolean;
  hasCreditStates: boolean;
  hasSelfServePaygo: boolean;
  isCreditPeriodExpired: boolean;
}): SandboxesBannerState[] {
  const states: SandboxesBannerState[] = [];

  if (hasSpendCap && safetyLimit > 0 && usage >= safetyLimit) {
    states.push('spendCapReached');
  }

  const hadCredits = freeLimit > 0;
  const consumedBalance = usage > 0;
  const isExhausted = hadCredits && consumedBalance && usage >= freeLimit;
  if (hasCreditStates && !isCreditPeriodExpired && isExhausted) {
    if (!hasSelfServePaygo) {
      states.push('creditsExhaustedContactSales');
    } else if (!isUsagePaid) {
      states.push('paused');
    }
  }

  return states;
}

export const SandboxesEnforcementBanner =
  createDismissibleBanner<SandboxesEnforcementBannerProps>({
    id: ({organizationId, bannerState}) => {
      return `sandboxes-${bannerState}-banner-${organizationId}`;
    },
    showAfter: DEFAULT_BANNER_SHOW_AFTER,
    Component: ({
      organizationId,
      bannerState,
      boldStatusMsg,
      statusMsg,
      cta,
      planName,
      onDismiss,
    }) => {
      return (
        <Tailwind>
          <AccountStatusBanner
            eventData={{
              location: `${bannerState} for Sandboxes alert banner`,
              organizationId,
              resource: 'Sandboxes',
              planName,
            }}>
            <AccountStatusBannerFlexWrapper>
              <div>
                <Icon name="info" />
              </div>
              <AccountStatusBannerTextWrapper>
                <span>
                  <AccountStatusBannerBoldText>
                    {boldStatusMsg}
                  </AccountStatusBannerBoldText>
                  {statusMsg != null && <span> {statusMsg}</span>}
                </span>
              </AccountStatusBannerTextWrapper>
            </AccountStatusBannerFlexWrapper>

            {cta}
            <Button
              variant="ghost"
              onClick={onDismiss}
              className="text-moon-800 hover:bg-white/[.20]">
              Dismiss
            </Button>
          </AccountStatusBanner>
        </Tailwind>
      );
    },
  });
